import { CalendarDays, MapPin } from "lucide-react";
import { effectiveOccurrence, formatEventRange, formatEventStart } from "../utils/eventDomain";
import { useOccurrenceEngagement } from "../hooks/useOccurrenceEngagement";
import OccurrenceEngagementRow from "./OccurrenceEngagementRow";

// Home timeline event rows. Each takes one occurrence (the `data` of an
// event_start / event_end item, or /home's next_event) — the backend echoes
// the Event's details on it; what's shown is its effective values (this
// occurrence's overrides over the series), resolved by effectiveOccurrence.
// Clicking hands the occurrence to `onOpen`, which opens the shared Event
// focus view; the full Event is only loaded then.

// "Next event" banner, pinned above the timeline (never mixed into it).
export function NextEventBanner({ occurrence, onOpen }) {
  const details = effectiveOccurrence({ occurrence });
  return (
    <button
      type="button"
      onClick={() => onOpen(occurrence)}
      className="w-full max-w-[480px] mx-auto mb-4 block text-left rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 hover:bg-purple-100"
    >
      <p className="text-[11px] font-semibold tracking-wide text-purple-700">NEXT EVENT</p>
      <p className="font-semibold text-gray-900">{details.title}</p>
      <p className="text-sm text-gray-600">
        {formatEventRange(details.starts_at, details.ends_at, details.is_all_day, details.timezone)}
      </p>
      {details.location && (
        <p className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
          <MapPin size={12} />
          {details.location}
        </p>
      )}
    </button>
  );
}

// event_start: the occurrence began at this point in the timeline — a social
// card from its host. Two payload shapes arrive here: the full occurrence
// (host and attendees) and the preview's restricted one (a friend of the
// host: no event_id, ends_at, location or RSVP fields; title already
// effective). Only the occurrence id, title, creator, starts_at, timezone and
// is_all_day are required.
//
// Laid out like a post card: the event itself (which opens the preview), then
// its engagement strip below, outside the card. Engagement is read from
// occurrenceEngagementStore (seeded from Home's pages), so likes and comments
// made in the preview show here and vice versa; a card whose occurrence the
// viewer has since lost (the preview answered 404) is hidden.
export function HomeEventStartCard({ occurrence, onOpen }) {
  const entry = useOccurrenceEngagement(occurrence.id);
  if (entry?.revoked) return null;
  const engagement = entry ? entry.engagement : occurrence.engagement ?? null;
  const details = effectiveOccurrence({ occurrence });
  const host = occurrence.creator?.username;
  const when = details.ends_at
    ? formatEventRange(details.starts_at, details.ends_at, details.is_all_day, details.timezone)
    : formatEventStart(details.starts_at, details.is_all_day, details.timezone);
  return (
    <div className="w-full max-w-[480px] mx-auto mb-6">
      <button
        type="button"
        onClick={() => onOpen(occurrence)}
        className="w-full flex gap-3 text-left rounded-xl border border-gray-200 px-4 py-3 hover:bg-gray-50"
      >
        <CalendarDays size={20} className="mt-0.5 shrink-0 text-purple-600" />
        <div className="min-w-0">
          <p className="text-xs text-gray-500">
            {new Date(details.starts_at).toLocaleDateString(undefined, {
              weekday: "long",
              // An all-day day is read in the event's timezone.
              timeZone: details.is_all_day ? details.timezone : undefined,
            })}
          </p>
          <p className="font-semibold text-gray-900 truncate">{details.title}</p>
          <p className="text-sm text-gray-600">{when}</p>
          {(details.location || host) && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {[details.location, host && `Hosted by ${host}`].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
      </button>
      {engagement && (
        <OccurrenceEngagementRow
          occurrenceId={occurrence.id}
          engagement={engagement}
          onOpenComments={() => onOpen(occurrence)}
          className="mt-2 px-2"
        />
      )}
    </div>
  );
}

// event_end: a quiet divider marking where the occurrence ended.
export function HomeEventEndMarker({ occurrence, onOpen }) {
  const details = effectiveOccurrence({ occurrence });
  return (
    <button
      type="button"
      onClick={() => onOpen(occurrence)}
      className="w-full max-w-[480px] mx-auto mb-6 block text-center"
    >
      <div className="flex items-center gap-2">
        <span className="h-px flex-1 bg-gray-200" />
        <span className="text-xs text-gray-500 whitespace-nowrap hover:underline">
          {details.title} ended
        </span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>
      <p className="text-[11px] text-gray-400 mt-0.5">
        {new Date(occurrence.ends_at).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })}
      </p>
    </button>
  );
}
